type RecordValue = Record<string, unknown>;
const TYPES: Record<string, string> = {add_to_cart:'ADD_TO_CART',remove_from_cart:'REMOVE_FROM_CART',begin_checkout:'CHECKOUT_START',purchase:'PURCHASE'};
const object = (value: unknown): RecordValue => value && typeof value === 'object' ? value as RecordValue : {};
const number = (value: unknown): number | undefined => value !== null && value !== '' && Number.isFinite(Number(value)) && Number(value) >= 0 ? Number(value) : undefined;
/** Read only Google's public ecommerce fields; never forward customer/address data. */
export function merchantProCommerce(value: unknown): RecordValue | undefined {
  const row = object(value);
  // MerchantPro publishes one authoritative object. gtag commands are fan-out
  // copies (GA4/Ads can use different IDs) and must not be counted again.
  const name = typeof row.event === 'string' ? row.event : '';
  const type = TYPES[name];
  if (!type) return;
  const data = object(row.ecommerce ?? row[2]);
  const rawItems = Array.isArray(data.items) ? data.items.slice(0,100) : [];
  const items = rawItems.map(value => {
    const item=object(value); return {item_id:String(item.item_id??'').slice(0,200),item_name:String(item.item_name??'').slice(0,200),
      price:number(item.price),quantity:Math.max(1,Math.round(number(item.quantity)??1))};
  }).filter(item => item.item_id);
  if (!items.length) return;
  const orderId = type === 'PURCHASE' ? String(data.transaction_id ?? '').trim().slice(0,200) : undefined;
  if (type === 'PURCHASE' && !orderId) return;
  const calculated = items.every(item=>item.price!==undefined) ? items.reduce((sum,item)=>sum+item.price!*item.quantity,0) : undefined;
  // MerchantPro includes coupon adjustments as lines without a product id.
  // They affect the checkout total, but must not become products/quantities.
  const hasAdjustments=rawItems.some(value=>{const item=object(value);return !item.item_id&&Number(item.price)<0;});
  const adjusted=hasAdjustments&&rawItems.every(value=>Number.isFinite(Number(object(value).price)))
    ? rawItems.reduce<number>((sum,value)=>{const item=object(value);return sum+Number(item.price)*Math.max(1,Number(item.quantity)||1);},0) : undefined;
  const amount=type==='CHECKOUT_START'&&adjusted!==undefined ? Math.max(0,Math.round(adjusted*100)/100) : number(data.value)??calculated;
  return {type,confirmationId:crypto.randomUUID(),orderId,quantity:items.reduce((sum,item)=>sum+item.quantity,0),
    value:amount,currency:typeof data.currency==='string'?data.currency.toUpperCase().slice(0,3):undefined,
    metadata:{surface:'merchantpro-confirmed',items,productName:items[0]?.item_name,merchantProProductId:items.length===1?items[0]?.item_id:undefined}};
}
/** Preserve MerchantPro/Google's own push handler and forward each native confirmation once. */
export function connectMerchantProCommerce(host: EventTarget, scope: Window = window): void {
  const global=scope as Window & {dataLayer?: unknown[]; smartyfindCommerceConnected?: boolean};
  if(global.smartyfindCommerceConnected) return;
  global.smartyfindCommerceConnected=true;
  const layer=global.dataLayer ?? (global.dataLayer=[]);
  const seen=new WeakSet<object>();
  const handle=(value:unknown)=>{
    if(!value || typeof value!=='object' || seen.has(value)) return;
    seen.add(value);
    const detail=merchantProCommerce(value);
    if(detail)host.dispatchEvent(new CustomEvent('smartyfind:track',{detail}));
  };
  const push=layer.push;
  layer.push=function(...values:unknown[]){const result=push.apply(this,values);for(const value of values)handle(value);return result;};
  for(const value of [...layer])handle(value);
}
